import React, { useEffect } from 'react';
import Animated, {
  cancelAnimation,
  interpolate,
  useAnimatedStyle,
  useSharedValue,
  withDelay,
  withRepeat,
  withTiming,
} from 'react-native-reanimated';

import type { LoadMainVariantProps } from '../types';
import { LOAD_MAIN_EASING } from '../loadMainMotion';

function Ripple({
  size,
  color,
  duration_ms,
  delay_ms,
}: {
  size: number;
  color: string;
  duration_ms: number;
  delay_ms: number;
}) {
  const progress = useSharedValue(0);

  useEffect(() => {
    progress.value = 0;
    progress.value = withDelay(
      delay_ms,
      withRepeat(
        withTiming(1, { duration: duration_ms, easing: LOAD_MAIN_EASING.soft }),
        -1,
        false,
      ),
    );
    return () => cancelAnimation(progress);
  }, [delay_ms, duration_ms, progress]);

  const ripple_style = useAnimatedStyle(() => ({
    width: size,
    height: size,
    borderRadius: size / 2,
    borderWidth: Math.max(1.5, size * 0.04),
    borderColor: color,
    opacity: interpolate(progress.value, [0, 0.7, 1], [0.65, 0.22, 0]),
    transform: [{ scale: interpolate(progress.value, [0, 1], [0.42, 1.18]) }],
  }));

  return <Animated.View style={[{ position: 'absolute' }, ripple_style]} />;
}

export function PulseLoader({ size, colors, duration_ms }: LoadMainVariantProps) {
  const core_size = size * 0.3;
  const pulse = useSharedValue(0);

  useEffect(() => {
    pulse.value = 0;
    pulse.value = withRepeat(
      withTiming(1, { duration: duration_ms, easing: LOAD_MAIN_EASING.smooth }),
      -1,
      true,
    );
    return () => cancelAnimation(pulse);
  }, [duration_ms, pulse]);

  const core_style = useAnimatedStyle(() => ({
    width: core_size,
    height: core_size,
    borderRadius: core_size / 2,
    backgroundColor: colors.primary,
    opacity: interpolate(pulse.value, [0, 1], [0.82, 1]),
    transform: [{ scale: interpolate(pulse.value, [0, 1], [0.92, 1.06]) }],
  }));

  return (
    <Animated.View
      style={{
        width: size,
        height: size,
        alignItems: 'center',
        justifyContent: 'center',
      }}>
      <Ripple size={size} color={colors.glow} duration_ms={duration_ms} delay_ms={0} />
      <Ripple
        size={size}
        color={colors.highlight}
        duration_ms={duration_ms}
        delay_ms={Math.round(duration_ms * 0.34)}
      />
      <Animated.View style={core_style} />
    </Animated.View>
  );
}
